/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BadgeStatusCOA } from '../../components/atoms/StatusLabel'
import { FileDown, Search, RotateCw } from 'lucide-react'
import {
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
  Card
} from 'react-bootstrap'
import Breadcrumb from '../../components/Label/Breadcrumb'
import TablePagination from '../../components/atoms/Table/TablePagination'
import {
  capitalizeFirstLetter,
  convertDateWithTime
} from '../../helpers/constants'
import Loading from '../../components/atoms/Loading'
import toast, { Toaster } from 'react-hot-toast'
import api from '../../config/AxiosInstance'
import { Loader } from 'react-bootstrap-typeahead'
import ModalStockAdjustment from '../../components/modal/stockAdjustment/ModalStockAdjustment'

const STATUSES = [
  { value: 'match', label: 'Match' },
  { value: 'not_match', label: 'Not Match' },
  { value: 'uncheck', label: 'Unchecked' }
]

const TYPES = [
  { value: 'checker', label: 'Checker' },
  { value: 'pic', label: 'PIC' }
]

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#888',
  textTransform: 'uppercase',
  marginBottom: '0.2rem',
  letterSpacing: '0.5px'
}

const valueStyle = {
  fontSize: '1rem',
  fontWeight: '500',
  color: '#333'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const filterLabelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

function DetailStockTake() {
  const { state } = useLocation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [countRender, setCountRender] = useState(0)
  const [loading, setLoading] = useState({
    page: false,
    table: false,
    closeTicket: false,
    download: false
  })
  const [filter, setFilter] = useState({
    q: '',
    status: '',
    type: '',
    name: ''
  })
  const [closeTicket, setCloseTicket] = useState(false)
  const [data, setData] = useState(null)
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const handleModalCloseTicket = () => {
    setCloseTicket(!closeTicket)
  }

  const renderAction = (dataValue) => (
    <div key={dataValue.id}>
      <button
        className="btn btn-secondary btn-sm"
        onClick={() =>
          navigate(
            `/stock-opname/stock-take/detail/${data?.id}/${dataValue?.id}`,
            {
              replace: true,
              state: {
                code: data.code,
                id: data.id,
                status: data.status,
                ...state,
                ...data
              }
            }
          )
        }
      >
        <Search size={14} />
      </button>
    </div>
  )

  const columns = [
    { key: 'slot', label: 'Slot' },
    { key: 'rack', label: 'Rack Name' },
    {
      key: 'rack_type',
      label: 'Rack Type',
      component: (data) => capitalizeFirstLetter(data.rack_type)
    },
    { key: 'checker', label: 'Checker' },
    { key: 'pic', label: 'PIC' },
    {
      key: 'status',
      label: 'Status',
      component: (data) => <BadgeStatusCOA text={data.status} />
    },
    {
      key: 'action',
      label: 'Action',
      component: (data) => renderAction(data),
      className: 'text-end'
    }
  ]

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  const getProgressAccuracy = useMemo(() => {
    const checkerTotalSlot =
      Number(data?.progress?.checker_progress?.total_slot) || 0
    const checkerTotalChecked =
      Number(data?.progress?.checker_progress?.total_checked) || 0
    const checkerPercentage =
      checkerTotalSlot > 0
        ? Math.round((checkerTotalChecked / checkerTotalSlot) * 100)
        : 0

    const picTotalSlot = Number(data?.progress?.pic_progress?.total_slot) || 0
    const picTotalChecked =
      Number(data?.progress?.pic_progress?.total_checked) || 0
    const picPercentage =
      picTotalSlot > 0 ? Math.round((picTotalChecked / picTotalSlot) * 100) : 0

    const totalSlot = Number(data?.progress?.precentage?.total_slot) || 0
    const locMatch = Number(data?.progress?.location_accuracy?.match) || 0
    const locationAccuracyPercentage =
      totalSlot > 0 ? Math.round((locMatch / totalSlot) * 100) : 0

    const totalSystem =
      Number(data?.progress?.stock_accuracy?.total_system) || 0
    const totalChecker =
      Number(data?.progress?.stock_accuracy?.total_checker) || 0
    const stockAccuracyPercentage =
      totalSystem > 0 ? Math.round((totalChecker / totalSystem) * 100) : 0

    return {
      checkerLabel: `${checkerTotalChecked}/${checkerTotalSlot} (${checkerPercentage}%)`,
      checkerPercentage,
      picLabel: `${picTotalChecked}/${picTotalSlot} (${picPercentage}%)`,
      picPercentage,
      locationAccuracyPercentage: String(locationAccuracyPercentage),
      stockAccuracyPercentage: String(stockAccuracyPercentage)
    }
  }, [data])

  const getOptions = useMemo(() => {
    if (filter?.type === 'checker') {
      return data?.user?.checker || []
    }
    if (filter?.type === 'pic') {
      return data?.user?.pic || []
    }
    return []
  }, [filter?.type, data])

  const handleChangeFilter = (e) => {
    const { name, value } = e.target

    setFilter((prevFilter) => {
      let updatedFilter = {
        ...prevFilter,
        [name]: value
      }

      if (name === 'type' && prevFilter.name !== '') {
        updatedFilter.name = ''
      }

      return updatedFilter
    })
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleResetFilter = () => {
    const resetFilter = { status: '', type: '', name: '', q: '' }
    setFilter(resetFilter)
  }

  const fetchData = async (customFilter, isCloseTicket = false) => {
    try {
      if (!isCloseTicket) {
        if (countRender === 0) {
          setLoading((prevLoading) => ({
            ...prevLoading,
            page: true
          }))
        } else {
          setLoading((prevLoading) => ({
            ...prevLoading,
            table: true
          }))
        }
      } else {
        setLoading((prevLoading) => ({
          ...prevLoading,
          page: true
        }))
      }
      const usedFilter = customFilter || filter
      let params = {
        page: meta.current_page,
        per_page: meta.per_page,
        q: usedFilter.q,
        status: usedFilter.status || null
      }
      if (usedFilter?.type !== '') {
        params = {
          ...params,
          [usedFilter?.type]: usedFilter?.name
        }
      }
      const res = await api.get(`/stock-opname/${id}`, { params })
      const { status, data: dataResponse } = res
      if (status === 200 && dataResponse.success) {
        if (!isCloseTicket) {
          if (countRender === 0) {
            setData({
              ...dataResponse.data,
              warehouse_name: state?.warehouse_name,
              status: dataResponse?.data?.status || 'waiting'
            })
          } else {
            setData((prevData) => ({
              ...prevData,
              slots: dataResponse?.data?.slots || []
            }))
          }
        } else {
          setData({
            ...dataResponse.data,
            warehouse_name: state?.warehouse_name,
            status: dataResponse?.data?.status || 'waiting'
          })
        }
        setMeta({
          per_page: dataResponse.metadata.per_page,
          current_page: dataResponse.metadata.current_page,
          total_row: dataResponse.metadata.total_row,
          total_page: dataResponse.metadata.total_page
        })
      } else {
        throw new Error(dataResponse.message)
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        err?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoading((prevLoading) => ({
        ...prevLoading,
        page: false,
        table: false
      }))
      setCountRender((prevCount) => prevCount + 1)
    }
  }

  const handleSubmitCloseTicket = async () => {
    try {
      setLoading((prevLoading) => ({
        ...prevLoading,
        closeTicket: true
      }))
      const res = await api.put(`/stock-opname/${id}/closed`)
      const { status } = res
      if (status === 200) {
        toast.success('Successfully closed ticket', {
          position: 'top-right',
          style: { fontSize: '14px', fontWeight: '600' }
        })
        fetchData(null, true)
        handleModalCloseTicket()
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    } finally {
      setLoading((prevLoading) => ({
        ...prevLoading,
        closeTicket: false
      }))
    }
  }

  const handleDownload = async () => {
    try {
      setLoading((prev) => ({
        ...prev,
        download: true
      }))
      const res = await api.get(`/stock-opname/${id}/export`, {
        responseType: 'arraybuffer'
      })
      const code = data?.code
      const { status, data: dataResponse } = res
      if (status === 200) {
        const blob = new Blob([dataResponse], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `stock_take_${code || '-'}.xlsx`)
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    } finally {
      setLoading((prev) => ({
        ...prev,
        download: false
      }))
    }
  }

  useEffect(() => {
    if (id) {
      const delayDebounceFn = setTimeout(() => {
        fetchData(filter)
      }, 500)
      return () => clearTimeout(delayDebounceFn)
    } else {
      toast.error('Stock opname not found!', {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    }
  }, [id, meta.current_page, meta.per_page, filter])

  const renderInfoItem = (label, value) => (
    <Col md={3}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value}</div>
    </Col>
  )

  if (loading.page) {
    return <Loading />
  }

  return (
    <Container fluid className="px-4">
      <Toaster />
      <Row className="mb-4 align-items-center">
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Breadcrumb
                label={data?.code || ''}
                link="/stock-opname/stock-take"
                back="STOCK TAKE"
                type="BadgeStatusStockTake"
                status={data?.status}
                stateValue={{ filter_warehouse_id: state?.filter_warehouse_id }}
              />
            </div>
            <div className="d-flex gap-2">
              {!['finish', 'closed', 'done'].includes(data?.status) && (
                <Button
                  variant="danger"
                  className="rounded px-4 fw-bold"
                  onClick={handleModalCloseTicket}
                >
                  Close Ticket
                </Button>
              )}
              {data?.status === 'done' && (
                <Button
                  variant={loading.download ? 'secondary' : 'warning'}
                  className={`rounded px-4 fw-bold ${
                    loading.download ? 'opacity-25' : 'text-white'
                  }`}
                  disabled={loading.download}
                  onClick={() => handleDownload()}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <FileDown size={16} />
                    <span>
                      {loading.download ? 'Exporting...' : 'Export Excel'}
                    </span>
                  </div>
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card style={{ ...cardStyle, height: '100%' }}>
            <h6 style={headerTitleStyle} className="mb-4 text-dark">
              Detail Information
            </h6>
            <Row className="mb-4">
              {renderInfoItem('Warehouse', data?.warehouse_name || '-')}
              {renderInfoItem('Checker', data?.checker || '-')}
              {renderInfoItem('PIC', data?.pic || '-')}
            </Row>
            <Row>
              <Col md={4}>
                <div style={labelStyle}>CHECKER PROGRESS</div>
                <div className="position-relative mt-2">
                  <div
                    className="rounded"
                    style={{ height: '32px', backgroundColor: '#f1f3f5' }}
                  >
                    <div
                      className="rounded h-100 transition-all"
                      style={{
                        width: `${getProgressAccuracy?.checkerPercentage}%`,
                        backgroundColor: '#ff9800',
                        transition: 'width 0.5s ease-out'
                      }}
                    />
                  </div>
                  <div className="position-absolute top-0 start-50 translate-middle-x h-100 d-flex align-items-center">
                    <span
                      className="fw-bold"
                      style={{
                        fontSize: '12px',
                        color: '#ffffff',
                        mixBlendMode: 'difference'
                      }}
                    >
                      {getProgressAccuracy?.checkerLabel}
                    </span>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div style={labelStyle}>PIC PROGRESS</div>
                <div className="position-relative mt-2">
                  <div
                    className="rounded"
                    style={{ height: '32px', backgroundColor: '#f1f3f5' }}
                  >
                    <div
                      className="rounded h-100 transition-all"
                      style={{
                        width: `${getProgressAccuracy?.picPercentage}%`,
                        backgroundColor: '#ff9800',
                        transition: 'width 0.5s ease-out'
                      }}
                    />
                  </div>
                  <div className="position-absolute top-0 start-50 translate-middle-x h-100 d-flex align-items-center">
                    <span
                      className="fw-bold"
                      style={{
                        fontSize: '12px',
                        color: '#ffffff',
                        mixBlendMode: 'difference'
                      }}
                    >
                      {getProgressAccuracy?.picLabel}
                    </span>
                  </div>
                </div>
              </Col>
              <Col md={2}>
                <div style={labelStyle}>LOCATION ACCURACY</div>
                <div className="d-flex align-items-end mt-1">
                  <span
                    style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#333'
                    }}
                  >
                    {getProgressAccuracy?.locationAccuracyPercentage}%
                  </span>
                </div>
              </Col>
              <Col md={2}>
                <div style={labelStyle}>STOCK ACCURACY</div>
                <div className="d-flex align-items-end mt-1">
                  <span
                    style={{
                      fontSize: '32px',
                      fontWeight: '700',
                      color: '#333'
                    }}
                  >
                    {getProgressAccuracy?.stockAccuracyPercentage}%
                  </span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Card style={cardStyle}>
        <Row className="mb-3">
          <Col md={2} className="d-flex align-items-center">
            <Form.Label style={filterLabelStyle} className="mb-0">
              FILTER BY
            </Form.Label>
          </Col>
          <Col md={8}>
            <div className="d-flex gap-2">
              <div style={{ flex: 1 }}>
                <Form.Select
                  name="status"
                  style={FormControlStyle}
                  value={filter?.status || ''}
                  onChange={handleChangeFilter}
                >
                  <option value="">All Status</option>
                  {STATUSES.map((status) => (
                    <option key={status?.value} value={status?.value}>
                      {status?.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div style={{ flex: 1 }}>
                <Form.Select
                  name="type"
                  style={FormControlStyle}
                  value={filter?.type || ''}
                  onChange={handleChangeFilter}
                >
                  <option value="">All Type</option>
                  {TYPES.map((type) => (
                    <option key={type?.value} value={type?.value}>
                      {type?.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div style={{ flex: 1 }}>
                <Form.Select
                  name="name"
                  style={FormControlStyle}
                  value={filter?.name || ''}
                  onChange={handleChangeFilter}
                  disabled={!filter?.type}
                >
                  <option value="">Select Name</option>
                  {getOptions?.map((option) => (
                    <option key={option?.id} value={option?.id}>
                      {option?.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </Col>
          <Col md={2}>
            <Button
              variant="light"
              className="w-100 border bg-white"
              style={{ height: '45px', borderRadius: '10px' }}
              onClick={handleResetFilter}
            >
              <RotateCw size={16} className="me-2 text-secondary" />
              Reset
            </Button>
          </Col>
        </Row>

        <hr className="mb-4 mt-2" style={{ opacity: 0.1 }} />

        <Row className="mb-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center">
              <h6 className="text-uppercase text-muted opacity-75 mb-0">
                Slot list
              </h6>
            </div>
            <div style={{ width: '300px' }}>
              <InputGroup>
                <InputGroup.Text
                  className="bg-white border-end-0"
                  style={{ borderRadius: '10px 0 0 10px', height: '45px' }}
                >
                  <Search size={16} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  style={{
                    ...FormControlStyle,
                    borderLeft: '0',
                    borderRadius: '0 10px 10px 0'
                  }}
                  placeholder="Search..."
                  value={filter.q}
                  onChange={(e) => {
                    setFilter({ ...filter, q: e.target.value })
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                />
              </InputGroup>
            </div>
          </div>
        </Row>

        <Table responsive hover style={{ fontSize: '14px' }}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={tableHeaderStyle}
                  className={`p-3 align-middle ${column.className || ''}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading?.table ? (
              <tr>
                <td colSpan={columns.length} className="text-center p-4">
                  <Loader />
                </td>
              </tr>
            ) : data?.slots?.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center p-4">
                  No data found
                </td>
              </tr>
            ) : (
              data?.slots?.map((item, index) => (
                <tr key={index}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`p-3 align-middle ${column.className || ''}`}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      {column.component
                        ? column.component(item)
                        : item[column.key] || '-'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
        <div className="mt-4 pt-3 border-top">
          <div className="d-flex gap-4 mb-3">
            {data?.detail?.map((user) => (
              <div key={user.id}>
                <span className="text-muted fw-bold small text-uppercase">
                  {user?.name || '-'}:
                </span>
                <span className="fw-bold text-danger ms-2">
                  {user?.total_slot || 0}
                </span>
              </div>
            ))}
          </div>
          <div>
            <TablePagination
              meta={meta}
              handlePageChange={handlePageChange}
              handleOnChangeMeta={handleOnChangeMeta}
            />
          </div>
        </div>
      </Card>

      <Card style={cardStyle}>
        <Row>
          {renderInfoItem(
            'Created By',
            data?.created_by ? capitalizeFirstLetter(data?.created_by) : '-'
          )}
          {renderInfoItem(
            'Created At',
            data?.created_at
              ? convertDateWithTime(data?.created_at, 'DD-MM-YYYY, HH:mm')
              : '-'
          )}
          {renderInfoItem(
            'Last Updated By',
            data?.updated_by ? capitalizeFirstLetter(data?.updated_by) : '-'
          )}
          {renderInfoItem(
            'Last Updated At',
            data?.updated_at
              ? convertDateWithTime(data?.updated_at, 'DD-MM-YYYY, HH:mm')
              : '-'
          )}
        </Row>
      </Card>

      <ModalStockAdjustment
        show={closeTicket}
        onHide={handleModalCloseTicket}
        title=""
        size="md"
        backdrop="static"
      >
        <Modal.Body className="my-2">
          <>
            <div className="p-2 mt-3 text-center">
              <div className="d-flex justify-content-center">
                <svg
                  width="181"
                  height="180"
                  viewBox="0 0 181 180"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M90.5 0C40.7991 0 0.5 40.2991 0.5 90C0.5 139.701 40.7991 180 90.5 180C140.201 180 180.5 139.701 180.5 90C180.5 40.2991 140.201 0 90.5 0ZM90.5 164.732C49.2366 164.732 15.7679 131.263 15.7679 90C15.7679 48.7366 49.2366 15.2679 90.5 15.2679C131.763 15.2679 165.232 48.7366 165.232 90C165.232 131.263 131.763 164.732 90.5 164.732Z"
                    fill="#828282"
                  />
                  <path
                    d="M80.8574 125.357C80.8574 127.915 81.8734 130.367 83.6817 132.176C85.4901 133.984 87.9428 135 90.5003 135C93.0577 135 95.5104 133.984 97.3188 132.176C99.1272 130.367 100.143 127.915 100.143 125.357C100.143 122.8 99.1272 120.347 97.3188 118.539C95.5104 116.73 93.0577 115.714 90.5003 115.714C87.9428 115.714 85.4901 116.73 83.6817 118.539C81.8734 120.347 80.8574 122.8 80.8574 125.357ZM85.6789 102.857H95.3217C96.2056 102.857 96.9289 102.134 96.9289 101.25V46.6071C96.9289 45.7232 96.2056 45 95.3217 45H85.6789C84.7949 45 84.0717 45.7232 84.0717 46.6071V101.25C84.0717 102.134 84.7949 102.857 85.6789 102.857Z"
                    fill="#828282"
                  />
                </svg>
              </div>
              <div className="my-3 fw-bold">
                <p>Close Ticket?</p>
                <p>
                  This ticket will be closed and all on going process will be
                  terminated
                </p>
              </div>
            </div>
          </>
          <Modal.Footer className="mt-3" style={{ height: '20px' }}>
            <hr />
            <div className="d-flex justify-end gap-2">
              <Button
                id="cancel-close-ticket"
                variant="transparent"
                className="text-muted"
                onClick={handleModalCloseTicket}
                disabled={loading.closeTicket}
              >
                Cancel
              </Button>
              <Button
                id="save-close-ticket"
                variant={`${loading.closeTicket ? 'secondary' : 'danger'}`}
                className={`${loading.closeTicket ? 'disabled' : ''}`}
                onClick={() => handleSubmitCloseTicket()}
                disabled={loading.closeTicket}
              >
                {loading.closeTicket ? 'Loading...' : 'Close'}
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Body>
      </ModalStockAdjustment>
    </Container>
  )
}

export default DetailStockTake
